import React, { useState } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Mail, Trash2, Archive, CheckCircle, XCircle, Download } from 'lucide-react';
import { toast } from 'sonner';

export function BulkProposalActions({ proposals, onRefresh }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [processing, setProcessing] = useState(false);

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === proposals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(proposals.map(p => p.id)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) {
      toast.error('No proposals selected');
      return;
    }

    setProcessing(true);
    const selectedProposals = proposals.filter(p => selectedIds.has(p.id));

    try {
      switch (action) {
        case 'send_email':
          await handleBulkEmail(selectedProposals);
          break;
        case 'mark_sent':
          await handleBulkStatusUpdate(Array.from(selectedIds), 'sent');
          break;
        case 'mark_won':
          await handleBulkStatusUpdate(Array.from(selectedIds), 'won');
          break;
        case 'mark_declined':
          await handleBulkStatusUpdate(Array.from(selectedIds), 'declined');
          break;
        case 'delete':
          await handleBulkDelete(Array.from(selectedIds));
          break;
        case 'download_pdfs':
          await handleBulkDownload(selectedProposals);
          break;
        default:
          break;
      }
      
      setSelectedIds(new Set());
      onRefresh();
    } catch (error) {
      toast.error(`Bulk action failed: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkEmail = async (proposals) => {
    // This would open a bulk email dialog
    toast.info(`Bulk email feature: ${proposals.length} proposals selected`);
    // TODO: Implement bulk email modal
  };

  const handleBulkStatusUpdate = async (ids, status) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('token');
    
    const promises = ids.map(id =>
      fetch(`${API_URL}/api/proposals/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      })
    );

    await Promise.all(promises);
    toast.success(`${ids.length} proposal(s) marked as ${status}`);
  };

  const handleBulkDelete = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} proposal(s)? This cannot be undone.`)) {
      return;
    }

    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('token');
    
    const promises = ids.map(id =>
      fetch(`${API_URL}/api/proposals/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    );

    await Promise.all(promises);
    toast.success(`${ids.length} proposal(s) deleted`);
  };

  const handleBulkDownload = async (proposals) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('token');

    for (const proposal of proposals) {
      try {
        const response = await fetch(`${API_URL}/api/proposals/${proposal.id}/pdf`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Download failed');
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `proposal_${proposal.company_name?.replace(/\s/g, '_')}_${proposal.id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Failed to download proposal ${proposal.id}`, error);
      }
    }
    
    toast.success(`Downloaded ${proposals.length} PDF(s)`);
  };

  const BulkActionBar = () => {
    if (selectedIds.size === 0) return null;

    return (
      <Card className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 shadow-2xl border-2 border-teal-500">
        <div className="flex items-center gap-4 px-6 py-4">
          <Badge variant="default" className="text-sm">
            {selectedIds.size} selected
          </Badge>
          
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleBulkAction('send_email')}
              disabled={processing}
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Emails
            </Button>
            
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleBulkAction('download_pdfs')}
              disabled={processing}
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDFs
            </Button>
            
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleBulkAction('mark_sent')}
              disabled={processing}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark Sent
            </Button>
            
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleBulkAction('mark_won')}
              disabled={processing}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark Won
            </Button>
            
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleBulkAction('mark_declined')}
              disabled={processing}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Decline
            </Button>
            
            <Button 
              size="sm" 
              variant="destructive"
              onClick={() => handleBulkAction('delete')}
              disabled={processing}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>

          <Button 
            size="sm" 
            variant="ghost"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear
          </Button>
        </div>
      </Card>
    );
  };

  return {
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    isSelected: (id) => selectedIds.has(id),
    selectedCount: selectedIds.size,
    BulkActionBar,
    clearSelection: () => setSelectedIds(new Set())
  };
}
